// AI Sales Brain tab inside the Lead workspace: Merchant Materials → Kaily's Direction → evidence-based analysis.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Upload, Link2, StickyNote, Trash2, Lock, Unlock, FileText, History, HelpCircle, AlertTriangle } from "lucide-react";
import { API } from "./SalesCRM";
import ConceptsPanel from "./ConceptsPanel";

export const CARD_TITLES = { snapshot: "Brand Snapshot", gap: "Current Content Gap", opportunity: "Commercial Opportunity",
    service: "Recommended Service", pitch_angle: "Suggested Pitch Angle", risks: "Risks / Missing Information" };
const LABELS = [["instagram", "Instagram"], ["google_maps", "Google Maps"], ["menu", "Menu"], ["website", "Website"],
    ["whatsapp", "WhatsApp"], ["facebook", "Facebook"], ["tiktok", "TikTok"], ["xiaohongshu", "小红书"], ["other", "Other"]];
const labelOf = (v) => (LABELS.find(([k]) => k === v) || [])[1] || v;

export function Box({ title, children, testid, tone = "" }) {
    return (
        <div className={`bg-white rounded-xl border p-4 ${tone || "border-neutral-200"}`} data-testid={testid}>
            {title && <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">{title}</div>}
            {children}
        </div>
    );
}

export function OppCard({ id, k, card, reload }) {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState("");
    const bullets = card.edited || card.ai || [];
    const shown = expanded ? bullets : bullets.slice(0, 5);
    const save = async () => {
        const arr = text.split("\n").map((s) => s.replace(/^[·\-\s]+/, "").trim()).filter(Boolean);
        await axios.patch(`${API}/crm/leads/${id}/opportunity`, { card: k, bullets: arr });
        setEditing(false); reload(); toast.success("Saved");
    };
    return (
        <Box title={CARD_TITLES[k]} testid={`opp-card-${k}`}>
            {card.edited && <span className="text-[9px] uppercase bg-lime-100 text-cafe-olive px-1.5 py-0.5 rounded-full">Edited by Kaily</span>}
            {!card.edited && card.ai && <span className="text-[9px] uppercase bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded-full">AI generated</span>}
            {editing ? (<>
                <textarea data-testid={`opp-edit-${k}`} value={text} onChange={(e) => setText(e.target.value)} rows={4} className="w-full border rounded-lg px-2 py-1.5 text-sm mt-2" />
                <div className="flex gap-2 mt-1">
                    <button data-testid={`opp-save-${k}`} onClick={save} className="text-xs px-3 py-1 rounded-full bg-cafe-espresso text-white">Save</button>
                    <button onClick={() => setEditing(false)} className="text-xs px-3 py-1 rounded-full border">Cancel</button>
                </div>
            </>) : (<>
                <ul className="text-sm space-y-1 mt-2">{shown.map((b, i) => <li key={i} className={/not confirmed/i.test(b) ? "text-amber-700" : ""}>· {b}</li>)}</ul>
                {!bullets.length && <div className="text-xs text-neutral-400 mt-1">Not analysed yet.</div>}
                <div className="flex gap-3 mt-2 text-xs">
                    {bullets.length > 5 && <button onClick={() => setExpanded(!expanded)} className="underline text-neutral-500">{expanded ? "Less" : "More"}</button>}
                    <button data-testid={`opp-edit-btn-${k}`} onClick={() => { setText(bullets.join("\n")); setEditing(true); }} className="underline text-cafe-olive">Edit</button>
                </div>
            </>)}
        </Box>
    );
}

function MaterialsPanel({ id, materials, legacyImages, reload }) {
    const [label, setLabel] = useState("instagram");
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(false);
    const [link, setLink] = useState("");
    const [note, setNote] = useState("");
    const fileRef = useRef(null);

    const upload = useCallback(async (files) => {
        const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf" || /\.pdf$/i.test(f.name));
        if (!list.length) return toast.error("Only images or PDF files");
        setBusy(true);
        const fd = new FormData();
        list.forEach((f) => fd.append("files", f, f.name || `pasted-${Date.now()}.png`));
        fd.append("source_label", label);
        try {
            const { data } = await axios.post(`${API}/crm/leads/${id}/materials`, fd);
            toast.success(`${data.added.length} file(s) added${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
            reload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        finally { setBusy(false); }
    }, [id, label, reload]);

    useEffect(() => {
        const onPaste = (e) => {
            const files = Array.from(e.clipboardData?.files || []);
            if (files.length) { e.preventDefault(); upload(files); }
        };
        window.addEventListener("paste", onPaste);
        return () => window.removeEventListener("paste", onPaste);
    }, [upload]);

    const addLink = async (kind, text) => {
        if (!text.trim()) return;
        try {
            await axios.post(`${API}/crm/leads/${id}/materials/link`, { kind, text, source_label: label });
            kind === "url" ? setLink("") : setNote(""); reload(); toast.success(kind === "url" ? "URL added" : "Note added");
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };
    const remove = async (mid) => {
        await axios.delete(`${API}/crm/leads/${id}/materials/${mid}`).catch(() => toast.error("Remove failed"));
        reload();
    };

    return (
        <Box title={`Merchant Materials (${materials.length + legacyImages.length})`} testid="materials-box">
            <div data-testid="materials-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
                className={`border-2 border-dashed rounded-xl p-4 text-center text-xs transition-colors ${drag ? "border-cafe-olive bg-lime-50" : "border-neutral-300 bg-neutral-50"}`}>
                <Upload size={16} className="mx-auto text-neutral-400 mb-1" />
                <div>Drag & drop screenshots / menu photos / PDFs here, <b>paste</b> (Ctrl/⌘+V) anywhere on this page, or</div>
                <div className="flex flex-wrap justify-center items-center gap-2 mt-2">
                    <select data-testid="materials-source-label" value={label} onChange={(e) => setLabel(e.target.value)} className="border rounded-full px-2 py-1 text-xs bg-white">
                        {LABELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button data-testid="materials-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="px-3 py-1 rounded-full bg-cafe-espresso text-white">{busy ? "Uploading…" : "Choose files"}</button>
                    <input ref={fileRef} data-testid="materials-file-input" type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
                </div>
            </div>
            {(materials.length > 0 || legacyImages.length > 0) && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3" data-testid="materials-grid">
                    {materials.filter((m) => m.kind === "image" || m.kind === "pdf").map((m, i) => (
                        <div key={m.id} className="relative group rounded-lg overflow-hidden border bg-neutral-100 aspect-square" data-testid={`material-${m.id}`}>
                            {m.thumb ? <img src={m.thumb} alt={m.filename} className="w-full h-full object-cover" /> : <FileText className="m-auto mt-8 text-neutral-400" />}
                            <span className="absolute left-1 top-1 text-[9px] px-1.5 py-0.5 rounded-full bg-black/60 text-white">{i + 1} · {labelOf(m.source_label)}{m.kind === "pdf" ? ` · PDF ${m.pages?.length || 0}p` : ""}</span>
                            <button data-testid={`material-remove-${m.id}`} onClick={() => remove(m.id)} title="Remove" className="absolute right-1 bottom-1 p-1 rounded-full bg-white/90 text-red-600"><Trash2 size={11} /></button>
                        </div>
                    ))}
                    {legacyImages.map((u, i) => (
                        <div key={u} className="relative rounded-lg overflow-hidden border bg-neutral-100 aspect-square" data-testid={`legacy-image-${i}`}>
                            <img src={u} alt="" className="w-full h-full object-cover" />
                            <span className="absolute left-1 top-1 text-[9px] px-1.5 py-0.5 rounded-full bg-neutral-700 text-white">Imported (legacy)</span>
                        </div>
                    ))}
                </div>
            )}
            {materials.filter((m) => m.kind === "url" || m.kind === "note").map((m) => (
                <div key={m.id} className="flex items-start gap-2 text-xs mt-2 bg-neutral-50 rounded-lg px-2.5 py-1.5" data-testid={`material-${m.id}`}>
                    {m.kind === "url" ? <Link2 size={12} className="mt-0.5 shrink-0" /> : <StickyNote size={12} className="mt-0.5 shrink-0" />}
                    <span className="flex-1 break-all">{m.kind === "url" ? <a href={m.text} target="_blank" rel="noreferrer" className="underline">{m.text}</a> : m.text}
                        <span className="text-neutral-400"> · {labelOf(m.source_label)}</span></span>
                    <button data-testid={`material-remove-${m.id}`} onClick={() => remove(m.id)} className="text-red-600"><Trash2 size={11} /></button>
                </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                <div className="flex gap-1">
                    <input data-testid="materials-url-input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https:// Instagram / Maps / website URL" className="flex-1 border rounded-lg px-2 py-1.5 text-xs" />
                    <button data-testid="materials-url-add" onClick={() => addLink("url", link)} className="px-3 py-1.5 rounded-full border text-xs">Add URL</button>
                </div>
                <div className="flex gap-1">
                    <input data-testid="materials-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note about the merchant (e.g. WhatsApp reply)" className="flex-1 border rounded-lg px-2 py-1.5 text-xs" />
                    <button data-testid="materials-note-add" onClick={() => addLink("note", note)} className="px-3 py-1.5 rounded-full border text-xs">Add note</button>
                </div>
            </div>
            <div className="text-[10px] text-neutral-400 mt-2">Instagram links often can't be fetched automatically — add screenshots for accurate analysis. Files are kept on the Lead and carried into the Client Project.</div>
        </Box>
    );
}

function DirectionPanel({ id, direction, reload }) {
    const [text, setText] = useState(direction.text || "");
    const [showHist, setShowHist] = useState(false);
    useEffect(() => { setText(direction.text || ""); }, [direction.text]);
    const save = async (patch) => {
        try { await axios.put(`${API}/crm/leads/${id}/direction`, patch); reload(); return true; }
        catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); return false; }
    };
    const revs = [...(direction.revisions || [])].reverse();
    return (
        <Box title="Kaily's Direction — highest-priority input for the AI (never overwritten)" testid="kaily-direction-box" tone="border-cafe-terracotta/40">
            <textarea data-testid="kaily-direction-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} disabled={direction.locked}
                placeholder="Your concept, observation, offer idea, visual reference, pricing approach…"
                className={`w-full border border-cafe-terracotta/40 rounded-lg px-2.5 py-1.5 text-sm ${direction.locked ? "bg-neutral-100 text-neutral-500" : "bg-amber-50/40"}`} />
            <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                <button data-testid="kaily-direction-save" disabled={direction.locked || text.trim() === (direction.text || "").trim()}
                    onClick={async () => { if (await save({ text })) toast.success("Direction saved as new revision"); }}
                    className="px-3 py-1 rounded-full border border-cafe-terracotta text-cafe-terracotta disabled:opacity-40">Save direction</button>
                <button data-testid="kaily-direction-lock" onClick={async () => { if (await save({ locked: !direction.locked })) toast.success(direction.locked ? "Unlocked" : "Locked"); }}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full ${direction.locked ? "bg-cafe-espresso text-white" : "border"}`}>
                    {direction.locked ? <Lock size={11} /> : <Unlock size={11} />} {direction.locked ? "Locked" : "Lock"}
                </button>
                <span className="text-neutral-400">v{revs.length}{revs[0] ? ` · ${(revs[0].at || "").slice(0, 16).replace("T", " ")}` : " · not set"}</span>
                {revs.length > 1 && <button data-testid="kaily-direction-history" onClick={() => setShowHist(!showHist)} className="flex items-center gap-1 underline text-neutral-500"><History size={11} /> {showHist ? "Hide" : "History"}</button>}
            </div>
            {showHist && (
                <div className="mt-2 space-y-1.5" data-testid="direction-history">
                    {revs.slice(1).map((r) => (
                        <div key={r.v} className="text-xs bg-neutral-50 rounded-lg p-2">
                            <div className="flex justify-between text-neutral-400"><span>v{r.v} · {(r.at || "").slice(0, 16).replace("T", " ")}</span>
                                {!direction.locked && <button data-testid={`direction-restore-${r.v}`} onClick={() => setText(r.text)} className="underline">Load into editor</button>}</div>
                            <div className="mt-0.5 whitespace-pre-wrap">{r.text}</div>
                        </div>
                    ))}
                </div>
            )}
        </Box>
    );
}

function LegacyPanel({ legacy }) {
    const [open, setOpen] = useState(false);
    if (!legacy) return null;
    return (
        <Box title="Imported (legacy AI pipeline) — kept for reference, not regenerated" testid="legacy-import-box">
            <button data-testid="legacy-toggle" onClick={() => setOpen(!open)} className="text-xs underline text-neutral-600">
                {open ? "Hide" : "Show"} · {legacy.videos.length} video concept(s){legacy.quote_total ? ` · quote RM ${Math.round(legacy.quote_total).toLocaleString()}` : ""}{legacy.audit?.ratings ? " · audit" : ""}
            </button>
            {open && (
                <div className="mt-2 space-y-2 text-xs">
                    {legacy.pain?.en && <div className="bg-neutral-50 rounded-lg p-2"><b>Pain point:</b> {legacy.pain.en}</div>}
                    {legacy.audit?.ratings && <div className="bg-neutral-50 rounded-lg p-2"><b>Audit:</b> {Object.entries(legacy.audit.ratings).map(([k, v]) => `${k} ${v}★`).join(" · ")}</div>}
                    {legacy.videos.map((v) => (
                        <div key={v.no} className="border rounded-lg p-2" data-testid={`legacy-video-${v.no}`}>
                            <div className="font-medium">{v.no}. {v.title}</div>
                            <div className="text-neutral-500">{v.purpose}</div>
                            {v.hook && <div className="italic mt-0.5">"{v.hook}"</div>}
                            <div className="text-neutral-400">{v.scenes} scene(s)</div>
                        </div>
                    ))}
                    {legacy.close_plan?.today_action && <div className="bg-neutral-50 rounded-lg p-2"><b>Legacy next action:</b> {legacy.close_plan.today_action?.en || legacy.close_plan.today_action}</div>}
                </div>
            )}
        </Box>
    );
}

export default function BrainSection({ id, reload: reloadLead }) {
    const [data, setData] = useState(null);
    const [obs, setObs] = useState("");
    const load = useCallback(async () => {
        try { const { data: d } = await axios.get(`${API}/crm/leads/${id}/brain`); setData(d); }
        catch { toast.error("Could not load AI Sales Brain"); }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    const generating = data?.brain?.status === "generating";
    useEffect(() => {
        if (!generating) return;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [generating, load]);
    useEffect(() => { if (data?.brain?.status === "ready") reloadLead?.(); }, [data?.brain?.status, data?.opportunity?.analysed_at]); // eslint-disable-line

    if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
    const { brain, opportunity: opp, materials, legacy_images, direction, legacy, history } = data;
    const cards = opp.cards || {};
    const pc = opp.pitch_concept || {};
    const hasMaterials = materials.length + legacy_images.length > 0;

    const analyse = async () => {
        try {
            await axios.post(`${API}/crm/leads/${id}/brain/analyse`, { observations: obs });
            toast.success("AI Sales Brain is analysing in the background"); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Analysis failed to start"); }
    };

    return (
        <div className="space-y-3" data-testid="section-brain">
            <MaterialsPanel id={id} materials={materials} legacyImages={legacy_images} reload={load} />
            <DirectionPanel id={id} direction={direction} reload={load} />
            <Box title="Analyse with AI Sales Brain" testid="analyse-box">
                <textarea data-testid="analyse-observations" value={obs} onChange={(e) => setObs(e.target.value)} rows={2}
                    placeholder="Personal observations from your visit, Google Maps notes, what the owner said…"
                    className="w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-sm" />
                {!hasMaterials && !direction.text && <div className="text-xs text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> No materials or direction yet — results will be mostly "Not confirmed". Add screenshots first for a factual analysis.</div>}
                <div className="flex flex-wrap items-center gap-3 mt-2">
                    <button data-testid="analyse-btn" disabled={generating} onClick={analyse}
                        className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-cafe-espresso text-white disabled:opacity-60">
                        <Sparkles size={13} /> {generating ? `Analysing… ${brain.step} (${brain.progress}%)` : opp.analysed_at ? "Re-analyse (keeps your edits & direction)" : "Analyse with AI Sales Brain"}
                    </button>
                    {opp.analysed_at && <span className="text-[10px] text-neutral-400">v{opp.version || 1} · {(opp.analysed_at || "").slice(0, 16).replace("T", " ")} · {opp.inputs?.images || 0} image(s) read</span>}
                    {history.length > 0 && <span className="text-[10px] text-neutral-400">{history.length} previous version(s) kept</span>}
                </div>
                {generating && <div className="mt-2 h-1.5 rounded-full bg-neutral-200 overflow-hidden" data-testid="brain-progress"><div className="h-full bg-cafe-olive transition-all" style={{ width: `${brain.progress}%` }} /></div>}
                {brain.status === "error" && <div className="text-xs text-red-600 mt-2" data-testid="brain-error">Analysis failed: {brain.error} — try again.</div>}
            </Box>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.keys(CARD_TITLES).map((k) => <OppCard key={k} id={id} k={k} card={cards[k] || {}} reload={load} />)}
            </div>
            {(opp.not_confirmed?.length > 0 || opp.questions?.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Box title="Not confirmed from the materials" testid="not-confirmed-box" tone="border-amber-300">
                        <ul className="text-sm space-y-1 text-amber-800">{(opp.not_confirmed || []).map((x, i) => <li key={i}>· {x}</li>)}</ul>
                        {!opp.not_confirmed?.length && <div className="text-xs text-neutral-400">Everything stated was supported by evidence.</div>}
                    </Box>
                    <Box title="Questions to ask the merchant" testid="questions-box">
                        <ul className="text-sm space-y-1">{(opp.questions || []).map((x, i) => <li key={i} className="flex gap-1.5"><HelpCircle size={13} className="mt-0.5 shrink-0 text-cafe-olive" />{x}</li>)}</ul>
                    </Box>
                </div>
            )}
            {opp.evidence?.length > 0 && (
                <Box title="Evidence used" testid="evidence-box">
                    <ul className="text-xs space-y-1">{opp.evidence.map((e, i) => <li key={i}>· {e.fact} <span className="text-neutral-400">— {e.source}</span></li>)}</ul>
                </Box>
            )}
            {pc.hook && (
                <Box title="Pitch Concept (pre-sales hook)" testid="pitch-concept-box">
                    <div className="text-sm font-medium text-cafe-espresso">"{pc.hook}"</div>
                    <div className="text-sm mt-1.5"><b>15s concept:</b> {pc.concept15}</div>
                    <div className="text-sm mt-1"><b>Visual moments:</b> {(pc.visual_moments || []).join(" · ")}</div>
                    <div className="text-sm mt-1 text-cafe-olive"><b>Business result:</b> {pc.business_result}</div>
                </Box>
            )}
            <ConceptsPanel id={id} hasAnalysis={!!opp.analysed_at} />
            <LegacyPanel legacy={legacy} />
        </div>
    );
}
